import { useRef, useEffect, useState, useCallback } from "react";
import { Eraser } from "lucide-react";

/**
 * Simple drawable e-signature pad using HTML canvas.
 *
 * Props:
 *  - onChange(dataUrl | null)  — fires whenever the drawing changes; null when cleared
 *  - height (default 180)      — canvas display height in px
 *
 * The exported PNG is a black-on-transparent trimmed data URL suitable for
 * embedding into a PDF service agreement.
 */
export function SignaturePad({ onChange, height = 180 }) {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(false);

  // Set up the canvas at device pixel ratio for crisp lines.
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const cssWidth = wrapper.clientWidth;
    const cssHeight = height;
    const dpr = window.devicePixelRatio || 1;

    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    canvas.width = Math.floor(cssWidth * dpr);
    canvas.height = Math.floor(cssHeight * dpr);

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
  }, [height]);

  useEffect(() => {
    setupCanvas();
    const onResize = () => setupCanvas();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [setupCanvas]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    // Support both touch and mouse
    const touch = e.touches?.[0] || e.changedTouches?.[0];
    const clientX = touch ? touch.clientX : e.clientX;
    const clientY = touch ? touch.clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = getPoint(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const p = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    // Report the latest drawing to the parent
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL("image/png");
    onChange?.(dataUrl);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0); // reset any scaling
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setHasInk(false);
    onChange?.(null);
  };

  return (
    <div className="w-full" data-testid="signature-pad">
      <div
        ref={wrapperRef}
        className="relative rounded-xl border-2 border-dashed border-slate-300 bg-white overflow-hidden"
      >
        <canvas
          ref={canvasRef}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          className="block touch-none cursor-crosshair select-none"
          data-testid="signature-canvas"
          aria-label="Signature pad — sign here"
        />
        {!hasInk && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <p className="text-slate-300 text-sm italic">Sign here with your finger or mouse</p>
          </div>
        )}
        {/* Signature guideline */}
        <div className="pointer-events-none absolute left-4 right-4 bottom-6 border-t border-slate-200" />
      </div>
      <div className="flex items-center justify-between mt-2">
        <p className="text-[11px] text-slate-500">
          Draw your signature above — hold and drag to sign.
        </p>
        <button
          type="button"
          onClick={clear}
          disabled={!hasInk}
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-rose-600 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="signature-clear-btn"
        >
          <Eraser className="w-3.5 h-3.5" /> Clear
        </button>
      </div>
    </div>
  );
}
